import type { CustomerType } from "@origin-digital/sales-cart";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Alert, Box, Preloader, Stack } from "@origin-digital/ods-core";
import {
  type IFuelTypes,
  type PlanFuelSelection,
  removePlanSolar,
  setPlanFuel,
  setPlanSolar,
  YesNoE,
} from "@origin-digital/sales-common";
import { logger } from "@origin-digital/reporting-client";
import { isErgon, isErgonSolar } from "@origin-digital/sales-common-types-fns";
import {
  EnergyAddressLookup,
  UpliftedEnergyAddressLookup,
  useEnergyAddressLookup,
  NMIResolutionState,
  type ConnectionAddressObject,
} from "@origin-digital/sales-energy-address-lookup";

import { FUEL_DUAL, FUEL_ELEC, FUEL_GAS } from "src/consts";
import { ILookup } from "src/consts/interfaces";
import {
  CampaignPlans,
  CampaignPlansResult,
  getCampaignPlans,
} from "src/utils/campaignPlansAPI";
import { getAddressFuel } from "src/utils/addressFuel";
import { createSalesCart, createProspectMoveCart } from "src/utils/cartAPI";
import { getMovesCrossSellURL } from "src/utils/externalRoutes";
import { getCustomerType, getMoveCustomerType } from "src/utils/pageContext";
import { ErgonPatchAlert } from "src/components/ErgonPatchAlert/ErgonPatchAlert";
import { BusinessAddressDialog } from "./BusinessAddressDialog";
import { LookupErrorAlert } from "./LookupErrorAlert";

const AddressLookupFieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  button:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const PLAN_FUEL_BY_TYPE: Record<IFuelTypes, PlanFuelSelection> = {
  dual: FUEL_DUAL,
  electricity: FUEL_ELEC,
  gas: FUEL_GAS,
};

type PlanRequest = {
  id: number;
  addressId?: string;
  customerType: CustomerType;
  showBusinessNotice: boolean;
};

export const Lookup: React.FC<ILookup> = ({ ctaLabel, isCustomer }) => {
  const [campaign, setCampaign] = useState<{
    addressId: string;
    plans: CampaignPlans;
    customerType: CustomerType;
  }>();
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(false);
  const [planLoadError, setPlanLoadError] = useState(false);
  const [dismissedCesId, setDismissedCesId] = useState<string>();
  const [isBusinessDialogOpen, setIsBusinessDialogOpen] = useState(false);
  const cesLinkSource = useRef<HTMLDivElement>(null);
  const latestPlanRequestId = useRef(0);
  const hasSelectedAddress = useRef(false);
  const submitting = useRef(false);
  const [initialAddressId] = useState(
    () =>
      new URLSearchParams(window.location.search).get("originAddressId") ||
      undefined
  );
  // The URL address is restored on mount; it is not a new customer selection.
  const addressToRestore = useRef(initialAddressId);
  const {
    selectedAddress,
    unconfirmedAddress,
    nmiResolutionState,
    setSelectedEnergy,
    isAddressLookupInProgress,
  } = useEnergyAddressLookup();
  const address = selectedAddress ?? unconfirmedAddress;
  const customerType = getMoveCustomerType(address?.customerClass);
  const isCes = nmiResolutionState === NMIResolutionState.CES;
  const isCesReady =
    isCes && !!address?.originAddressId && !isAddressLookupInProgress;
  const isErgonAddress = !!address && isErgon(address);
  const isErgonSolarPatch = !!address && isErgonSolar(address);
  const resolutionComplete = [
    NMIResolutionState.Init,
    NMIResolutionState.Completed,
    NMIResolutionState.ExactMatchFound,
    NMIResolutionState.NmiHardStop,
  ].includes(nmiResolutionState);
  const campaignPlans =
    campaign?.addressId === address?.originAddressId &&
    campaign?.customerType === customerType
      ? campaign?.plans
      : undefined;
  // Only include electricity once the current address has a confirmed NMI.
  const plans = campaignPlans && {
    ...campaignPlans,
    electricity:
      address?.isExactMatchForNMI === true || address?.isSelected === true
        ? campaignPlans.electricity
        : undefined,
  };
  const emptyPlans = !!plans && !plans.electricity && !plans.gas;
  const addressFuel = address ? getAddressFuel(address) : undefined;
  const noAvailableEnergy = isCustomer
    ? emptyPlans
    : !!address?.originAddressId && !addressFuel;
  const isReadyToSubmit =
    !!address?.originAddressId &&
    !isAddressLookupInProgress &&
    resolutionComplete &&
    !isCes &&
    (isCustomer ? !!plans && !emptyPlans : !!addressFuel);

  const isResidentialToBusinessMove =
    getCustomerType() === "residential" && customerType === "business";
  const showBusinessAddressDialog =
    isResidentialToBusinessMove &&
    isReadyToSubmit &&
    // A hard stop can still have the existing meter-search dialog open.
    nmiResolutionState !== NMIResolutionState.NmiHardStop &&
    !isSubmitting &&
    isBusinessDialogOpen;

  const ctaBlocked = !(isCesReady || isReadyToSubmit) || isSubmitting;
  const ctaLoading =
    !isCes &&
    !isAddressLookupInProgress &&
    ((!!address && nmiResolutionState === NMIResolutionState.InProgress) ||
      (isCustomer && isLoadingPlans) ||
      isSubmitting);
  const showLookupError =
    globalError ||
    (noAvailableEnergy &&
      resolutionComplete &&
      !isAddressLookupInProgress &&
      !isCes &&
      !isErgonAddress);

  const closeBusinessAddressDialog = () => setIsBusinessDialogOpen(false);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Browser Back restores the submission lock along with the cached page.
        submitting.current = false;
        setIsSubmitting(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    // Resolve both fuels before service selection on cross-sell, for every customer state.
    setSelectedEnergy({ electricity: true, gas: true });
  }, [setSelectedEnergy]);

  useEffect(() => {
    return () => {
      latestPlanRequestId.current += 1;
    };
  }, []);

  const resetCampaign = useCallback(() => {
    hasSelectedAddress.current = false;
    setIsBusinessDialogOpen(false);
    latestPlanRequestId.current += 1;
    setCampaign(undefined);
    setIsLoadingPlans(false);
    setGlobalError(false);
    setPlanLoadError(false);
    removePlanSolar();
  }, []);

  useEffect(() => {
    if (isCes) resetCampaign();
  }, [isCes, resetCampaign]);

  const startAddressRequest = (
    nextAddress: ConnectionAddressObject
  ): PlanRequest => {
    const addressId = nextAddress.originAddressId;
    const nextCustomerType = getMoveCustomerType(nextAddress.customerClass);
    const isRestoringAddress =
      !!addressToRestore.current && addressId === addressToRestore.current;
    if (!isRestoringAddress) addressToRestore.current = undefined;

    const request = {
      id: ++latestPlanRequestId.current,
      addressId,
      customerType: nextCustomerType,
      showBusinessNotice:
        !isRestoringAddress &&
        !!addressId &&
        getCustomerType() === "residential" &&
        nextCustomerType === "business",
    };
    const loadingPlans = isCustomer && !!addressId;
    setIsBusinessDialogOpen(
      !loadingPlans &&
        request.showBusinessNotice &&
        !!getAddressFuel(nextAddress)
    );
    setGlobalError(false);
    setPlanLoadError(false);
    setCampaign(undefined);
    setIsLoadingPlans(loadingPlans);
    removePlanSolar();

    return request;
  };

  const completePlanRequest = (
    request: PlanRequest,
    nextPlans: CampaignPlansResult
  ) => {
    if (request.id !== latestPlanRequestId.current || !request.addressId)
      return;

    if (nextPlans.failed) {
      setPlanLoadError(true);
      setIsLoadingPlans(false);
      return;
    }

    setIsBusinessDialogOpen(
      request.showBusinessNotice && !!(nextPlans.electricity || nextPlans.gas)
    );
    setCampaign({
      addressId: request.addressId,
      plans: nextPlans,
      customerType: request.customerType,
    });
    setIsLoadingPlans(false);
  };

  const handleAddressInputChange = (
    event: React.FormEvent<HTMLFieldSetElement>
  ) => {
    if ((event.target as HTMLElement).id === "address-lookup") {
      addressToRestore.current = undefined;
    }
  };

  const handleAddressCleared = () => {
    addressToRestore.current = undefined;
    resetCampaign();
  };

  const handleAddressUpdate = async (nextAddress: ConnectionAddressObject) => {
    if (isCes) {
      resetCampaign();
      return;
    }
    const request = startAddressRequest(nextAddress);
    if (!isCustomer || !request.addressId) return;

    const nextPlans = await getCampaignPlans(
      request.addressId,
      nextAddress.elecDetails?.hasSolarPanels || false,
      request.customerType
    );
    completePlanRequest(request, nextPlans);
  };

  const handleAddressSelected = (nextAddress: ConnectionAddressObject) => {
    hasSelectedAddress.current = true;
    return handleAddressUpdate(nextAddress);
  };

  const handleAddressUnconfirmed = (nextAddress: ConnectionAddressObject) => {
    // Initial address details can arrive after the customer confirms a unit.
    if (hasSelectedAddress.current) return;

    return handleAddressUpdate(nextAddress);
  };

  const startSubmission = () => {
    submitting.current = true;
    setIsSubmitting(true);
    closeBusinessAddressDialog();
    setGlobalError(false);
  };

  const failSubmission = (error: unknown) => {
    setGlobalError(true);
    logger.error("Failed to create sales cart", error);
    submitting.current = false;
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    // CES takes precedence over every standard residential/business move path.
    if (isCesReady) {
      // The library owns searchTerm and isEspec in its provider context.
      // Reuse its primary link so the widget and popup always share a destination.
      const href = cesLinkSource.current
        ?.querySelector<HTMLAnchorElement>('a[href*="/ces?"]')
        ?.getAttribute("href");
      if (href) window.location.href = href;
      return;
    }

    if (
      submitting.current ||
      !isReadyToSubmit ||
      !address?.originAddressId ||
      (isCustomer && !plans)
    )
      return;

    startSubmission();
    setPlanSolar(address.elecDetails?.hasSolarPanels ? YesNoE.Yes : YesNoE.No);
    try {
      let fuel: IFuelTypes;
      if (isCustomer) {
        const cartFuel = await createSalesCart(address.originAddressId, plans, {
          customerType,
          isExistingCustomer: isCustomer,
        });
        if (!cartFuel) {
          throw new Error("No eligible energy plans in the saved cart");
        }
        fuel = cartFuel;
      } else {
        if (!addressFuel) {
          throw new Error("No available energy services at the address");
        }
        fuel = addressFuel;
        await createProspectMoveCart(address.originAddressId, customerType);
      }
      setPlanFuel(PLAN_FUEL_BY_TYPE[fuel]);
      window.location.href = getMovesCrossSellURL(
        address.originAddressId,
        isCustomer ? fuel : undefined,
        // Address meters indicate requested services, not plan availability.
        // A strict fuel restriction would restore unavailable fuels on Back.
        isCustomer
          ? undefined
          : {
              electricity: fuel === "electricity" || fuel === "dual",
              gas: fuel === "gas" || fuel === "dual",
              internet: customerType === "residential",
              lpg: customerType === "residential",
            }
      );
    } catch (error) {
      failSubmission(error);
    }
  };

  return (
    <Stack data-id="movers-widget-wp" space="small">
      {!isSubmitting && (
        <BusinessAddressDialog
          open={showBusinessAddressDialog}
          onClose={closeBusinessAddressDialog}
          onContinue={handleSubmit}
        />
      )}
      <AddressLookupFieldset
        data-hj-suppress={true}
        aria-label="New connection address"
        onChangeCapture={handleAddressInputChange}
        aria-busy={isSubmitting}
        disabled={isSubmitting}
      >
        <UpliftedEnergyAddressLookup
          ariaLabel="New connection address"
          placeholder="Enter your new address..."
          ctaLabel={isCesReady ? "Show available energy services" : ctaLabel}
          ctaBlocked={ctaBlocked}
          ctaLoading={ctaLoading}
          originAddressId={initialAddressId}
          onAddressCleared={handleAddressCleared}
          onAddressesUpdated={resetCampaign}
          onAddressSelected={handleAddressSelected}
          onAddressUnconfirmed={handleAddressUnconfirmed}
          onCtaClick={handleSubmit}
          searchByMirn={false}
        />
      </AddressLookupFieldset>
      {isSubmitting && (
        <Box
          display="flex"
          justifyContent="center"
          domProps={{ role: "status", "aria-label": "Saving your move" }}
        >
          <Preloader size="small" />
        </Box>
      )}
      <EnergyAddressLookup.BelowThresholdAlert />
      <EnergyAddressLookup.HardStopAlert />
      {planLoadError && (
        <Alert
          title="Something went wrong"
          tone="critical"
          data-id="campaign-plans-error"
        >
          We couldn’t load your plans. Please try your address again.
        </Alert>
      )}
      {/* Keep the provider's link available after the visible dialog is dismissed. */}
      <div ref={cesLinkSource} hidden>
        <EnergyAddressLookup.CesErrorAlert
          customerType={customerType}
          showAsDialog={false}
          open={false}
          onClose={() => {}}
        />
      </div>
      <EnergyAddressLookup.CesErrorAlert
        customerType={customerType}
        showAsDialog
        open={isCesReady && dismissedCesId !== address?.originAddressId}
        onClose={() => setDismissedCesId(address?.originAddressId)}
      />
      {showLookupError && <LookupErrorAlert />}
      <ErgonPatchAlert
        isErgon={isErgonAddress}
        isErgonSolar={isErgonSolarPatch}
        selectedFuel={{ electricity: true, gas: true }}
        emptyPlans={noAvailableEnergy}
      />
    </Stack>
  );
};
